// WebsiteDetails.js
import React, { useState } from 'react';

const WebsiteDetails = ({
  websiteUrl, // Prop from App.js (the validated/fetched URL)
  onFetchData, // Function from App.js to trigger data fetching
  faviconUrl, // Prop from App.js (fetched favicon)
  websiteName, // Prop from App.js (fetched website name)
  websiteTitle, // Prop from App.js (fetched website title)
  websiteFetchError, // Prop for fetch error from App.js
}) => {
  const [localWebsiteUrl, setLocalWebsiteUrl] = useState(websiteUrl || ''); // For input field
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inputError, setInputError] = useState(null); // For local input validation (e.g., empty URL)

  // Update local input state when websiteUrl prop from App.js changes
  // This ensures the input field reflects the URL from App.js after a fetch or from query params
  React.useEffect(() => {
    setLocalWebsiteUrl(websiteUrl || '');
  }, [websiteUrl]);


  const handleInputChange = (event) => {
    setLocalWebsiteUrl(event.target.value);
    setInputError(null); // Clear local input error when typing
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
  };

  const handleButtonMouseEnter = () => {
    setIsButtonHovered(true);
  };

  const handleButtonMouseLeave = () => {
    setIsButtonHovered(false);
  };

  const handleSubmit = async () => { // Make it async
    if (!localWebsiteUrl.trim()) {
      setInputError('Please enter a website URL.');
      return;
    }

    let formattedUrl = localWebsiteUrl.trim();
    if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = 'https://' + formattedUrl;
    }

    setIsLoading(true);
    setInputError(null); // Clear any previous local input error before fetching

    try {
      // Call the onFetchData function from the parent (App.js), passing the formatted URL
      await onFetchData(formattedUrl);
    } catch (err) {
      // Catch any unexpected errors during the onFetchData execution (e.g., network issues)
      // Most errors from Shopify validation will be handled by App.js and passed via websiteFetchError
      setInputError(err.message || 'An unexpected error occurred during fetch.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  // Combine local input validation error with the website fetch error from App.js
  const displayOverallError = inputError || websiteFetchError;

  // Conditionally render website info: only if websiteName is present (indicating successful data from App.js)
  // AND there's no overall error.
  const showWebsiteInfo = websiteName && !displayOverallError;

  return (
    <div className="website-details-container">
      <style jsx="true">{`
        /* Global styles for WebsiteDetails component */
        .website-details-container {
          padding: 2rem 0;
          margin-top: 1.5rem;
          text-align: center;
          max-width: 90%;
          margin: 1.5rem auto;
          box-sizing: border-box;
          word-wrap: break-word;
        }
        
        .input-container {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 1.5rem;
          align-items: center;
          justify-content: center;
        }
        
        @media (min-width: 640px) {
          .input-container {
            flex-direction: row;
          }
        }
        
        .input-field {
          padding: 0.75rem 1rem;
          border-radius: 0.375rem;
          border: 1px solid #d1d5db;
          flex-grow: 1;
          max-width: 100%;
          width: 100%;
          font-size: 1rem;
          transition: all 0.2s ease;
        }
        
        @media (min-width: 640px) {
          .input-field {
            max-width: 500px;
          }
        }
        
        .input-field:focus {
          outline: none;
          border-color: #007BFF;
          box-shadow: 0 0 0 3px rgba(0, 169, 157, 0.1);
        }
        
        .fetch-button {
          padding: 0.75rem 1.5rem;
          border-radius: 0.375rem;
          background-color: #f33a3a;
          color: white;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 600;
          transition: background-color 0.2s ease;
          width: 100%;
        }
        
        @media (min-width: 640px) {
          .fetch-button {
            width: auto;
          }
        }
        
        .fetch-button:hover {
          background-color: #e02e2e;
        }
        
        .fetch-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .website-info {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          margin: 1.5rem 0;
          padding: 1.5rem 0;
          border-top: 1px solid #e5e7eb;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .website-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .favicon {
          width: 40px;
          height: 40px;
          object-fit: cover;
          border-radius: 0.375rem;
          border: 1px solid #e5e7eb;
          transition: transform 0.2s ease-in-out;
          background-color: white;
          padding: 0.25rem;
        }
        
        .favicon:hover {
          transform: scale(1.1);
        }
        
        .favicon-placeholder {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          color: #9ca3af;
          background-color: white;
          border-radius: 0.375rem;
          border: 1px solid #e5e7eb;
        }
        
        .website-name {
          font-size: 1.5rem;
          margin: 0;
          color: #111827;
          word-wrap: break-word;
          font-weight: 600;
        }
        
        .website-title {
          font-size: 0.875rem;
          color: #4b5563;
          word-break: break-word;
          max-width: 90%;
          margin: 0;
          font-style: italic;
        }
        
        .website-url {
          font-size: 0.875rem;
          color: #007BFF;
          word-break: break-word;
          max-width: 90%;
          margin-top: 0.25rem;
        }
        
        .error-message {
          color: #f33a3a;
          margin-top: 0.5rem;
          font-size: 0.875rem;
          background-color: #fee2e2;
          padding: 0.5rem;
          border-radius: 0.375rem;
          border: 1px solid #fca5a5;
          display: inline-block;
        }
        
        .spinner {
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top: 3px solid white;
          width: 1rem;
          height: 1rem;
          animation: spin 1s linear infinite;
          margin-right: 0.5rem;
          display: inline-block;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .section-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 0.75rem;
          text-align: center;
        }
        
        .instructions {
          color: #6b7280;
          margin-bottom: 1.5rem;
          font-size: 0.875rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }
        
        .badge {
          display: inline-block;
          background-color: #007BFF;
          color: white;
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          border-radius: 9999px;
          margin-left: 0.5rem;
          font-weight: 600;
        }
      `}</style>

      <h2 className="section-title">
        Enter a Shopify Store URL
        <span className="badge">Step 1</span>
      </h2>
      
      <p className="instructions">
        Enter the URL of any Shopify store to extract its products and collections.
        We'll automatically detect the store information for you.
      </p>

      <div className="input-container">
        <input
          type="text"
          className="input-field"
          placeholder="Enter Shopify store URL (e.g., https://example.myshopify.com)"
          value={localWebsiteUrl}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyPress={handleKeyPress}
        />
        <button
          className="fetch-button"
          onClick={handleSubmit}
          onMouseEnter={handleButtonMouseEnter}
          onMouseLeave={handleButtonMouseLeave}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span className="spinner"></span>
              Fetching...
            </>
          ) : (
            'Fetch Data'
          )}
        </button>
      </div>

      {/* Display combined error message */}
      {displayOverallError && <p className="error-message">{displayOverallError}</p>}
      
      {/* Conditionally render website info based on App.js providing valid data and no errors */}
      {showWebsiteInfo && (
        <div className="website-info">
          <div className="website-header">
            {faviconUrl ? (
              <img 
                src={faviconUrl || "/placeholder.svg"} 
                alt="Store favicon" 
                className="favicon"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  const placeholder = document.createElement('div');
                  placeholder.className = 'favicon-placeholder';
                  placeholder.innerHTML = '🏪';
                  e.target.parentNode.appendChild(placeholder);
                }}
              />
            ) : (
              <div className="favicon-placeholder">🏪</div>
            )}
            <h2 className="website-name">{websiteName}</h2>
          </div>
          
          {websiteTitle && <p className="website-title">{websiteTitle}</p>}
          <p className="website-url">{websiteUrl}</p>
        </div>
      )}
    </div>
  );
};

export default WebsiteDetails;